<template>
  <AccountRateSheetDefinitionLineForm
  isRateSheetLevel
  disableAutoSelect
  :headerClass="headerClass"
  :account="account"
  :rateSheetDefinition="rateSheetDefinition"
  :rateSheetDefinitionLine="line"
  :selectedVersion="selectedVersion"
  @save="save"
  @save-and-close="saveAndClose"
  @cancel="$emit('cancel')">
    <template #context="{ line: builtLine }">
      <div class="mt-4">
        <template v-if="hasDimensions(builtLine)">
          <v-alert
          dense
          text
          :type="coverageAlertType(impactOf(builtLine).coverage)"
          :icon="baseRateMop.path"
          class="body-2 mb-2">
            {{ $tc('coversBaseRateHits', impactOf(builtLine).coverage.totalLogs, impactOf(builtLine).coverage) }}
          </v-alert>
          <v-alert
          v-if="impactOf(builtLine).linesTakenOver.length > 0"
          dense
          text
          type="warning"
          class="body-2 mb-2 centered-icon">
            <div>{{ $t('lineWillRepriceExistingLines') }}</div>
            <div v-for="takenOver in impactOf(builtLine).linesTakenOver" :key="takenOver.rateSheetLineId ?? takenOver.uuid">
              <span class="font-weight-medium">{{ rateSheetLineLabel(takenOver) }}</span> · {{ formatMoney(takenOver.rate) }}
            </div>
          </v-alert>
        </template>
        <v-checkbox
        v-if="definitionLines"
        id="base-rate-line-add-to-definition"
        dense
        hide-details
        class="mt-0"
        :inputValue="addToDefinition && !definitionHasLine(builtLine)"
        :disabled="definitionHasLine(builtLine)"
        :label="definitionHasLine(builtLine)
          ? $t('definitionAlreadyHasLine', { rateSheetDefinition: rateSheetDefinition.name })
          : $t('addToActiveRateSheetDefinition', { rateSheetDefinition: rateSheetDefinition.name })"
        @change="addToDefinition = $event"/>
      </div>
    </template>
  </AccountRateSheetDefinitionLineForm>
</template>

<script>
import { cloneDeep } from 'lodash'
import { formatMoney } from '@/utils/NumericMutations'
import { baseRateMop } from '@/images/AngelSvgLibrary'
import { coverageOf, lineDimensionIds, linesTakenOverBy, sameDimensions, specifiedDimensions } from '@/utils/RateSheetMatching'
import { rateSheetLineLabel } from '@/components/contract/base-rate-finder/BaseRateFinder'

export default {
  name: 'BaseRateLineEditor',

  components: {
    AccountRateSheetDefinitionLineForm: () => import('@/components/account/account-scaling/rate-sheet-definitions/AccountRateSheetDefinitionLineForm.vue')
  },

  props: {
    account: {
      type: Object,
      required: true
    },
    rateSheetDefinition: {
      type: Object,
      required: true
    },
    sheetLines: {
      type: Array,
      required: true
    },
    combinations: {
      type: Array,
      required: true
    },
    line: {
      type: Object,
      required: false,
      default: undefined
    },
    definitionLines: {
      type: Array,
      required: false,
      default: undefined
    },
    lookup: {
      type: Object,
      required: true
    },
    headerClass: {
      type: String,
      required: true
    }
  },

  data: () => ({
    baseRateMop,
    addToDefinition: false
  }),

  computed: {
    selectedVersion () {
      return { rateSheetLines: this.sheetLines }
    }
  },

  created () {
    this.cachedImpact = undefined
    this.addToDefinition = !!this.line?.addToDefinition
  },

  methods: {
    formatMoney,
    rateSheetLineLabel,

    hasDimensions (line) {
      return specifiedDimensions(line).length > 0
    },

    impactOf (line) {
      const dimensionKey = JSON.stringify(lineDimensionIds(line))
      const cached = this.cachedImpact
      if (cached?.dimensionKey === dimensionKey && cached.sheetLines === this.sheetLines &&
        cached.combinations === this.combinations && cached.lookup === this.lookup) {
        return cached.impact
      }

      const impact = {
        coverage: coverageOf(line, this.sheetLines, this.combinations, this.lookup),
        linesTakenOver: linesTakenOverBy(line, this.sheetLines, this.lookup)
      }
      this.cachedImpact = { dimensionKey, sheetLines: this.sheetLines, combinations: this.combinations, lookup: this.lookup, impact }
      return impact
    },

    coverageAlertType ({ coveredLogs, totalLogs }) {
      if (coveredLogs === 0) return 'warning'
      return coveredLogs === totalLogs ? 'success' : 'info'
    },

    definitionHasLine (line) {
      return this.definitionLines.some(definitionLine => sameDimensions(definitionLine, line))
    },

    stagedLine (line) {
      return {
        ...cloneDeep(line),
        addToDefinition: !!this.definitionLines && this.addToDefinition && !this.definitionHasLine(line)
      }
    },

    save (line) {
      this.$emit('save', this.stagedLine(line))
    },

    saveAndClose (line) {
      this.$emit('save-and-close', this.stagedLine(line))
    }
  }
}
</script>

<style scoped>
.centered-icon ::v-deep .v-alert__icon {
  align-self: center;
}
</style>
